import { Slot, Slottable } from '@radix-ui/react-slot';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'gold';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-contrast shadow-xs hover:bg-primary-hover active:bg-primary-active hover:shadow-glow',
  secondary: 'bg-primary-soft text-primary hover:bg-primary-soft-hover',
  ghost: 'text-text-muted hover:bg-surface-overlay hover:text-text',
  outline:
    'border border-line-strong bg-surface/60 text-text hover:border-primary/60 hover:bg-surface-overlay',
  danger: 'bg-danger-strong text-danger-contrast hover:brightness-110 active:brightness-95',
  gold: 'bg-gold-gradient text-gold-contrast shadow-xs hover:shadow-glow-gold',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 rounded-sm px-3 text-xs',
  md: 'h-10 gap-2 rounded-md px-4 text-sm',
  lg: 'h-12 gap-2.5 rounded-md px-6 text-base',
  icon: 'size-10 rounded-md',
  'icon-sm': 'size-8 rounded-sm',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  /** Renders the child element instead of a `button`, e.g. a router `Link`. */
  asChild?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    asChild = false,
    leadingIcon,
    trailingIcon,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const classNameResolved = cn(
    'relative inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap',
    'ease-out-quint transition-[background-color,box-shadow,color,border-color,transform] duration-[160ms]',
    'active:translate-y-px disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-2',
    VARIANTS[variant],
    SIZES[size],
    className,
  );
  const leading = isLoading ? <Spinner className="size-4" /> : leadingIcon;

  if (asChild) {
    return (
      <Slot
        ref={ref}
        data-loading={isLoading || undefined}
        aria-busy={isLoading || undefined}
        className={classNameResolved}
        {...props}
      >
        {leading}
        <Slottable>{children}</Slottable>
        {trailingIcon}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      data-loading={isLoading || undefined}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={classNameResolved}
      {...props}
    >
      {leading}
      {children}
      {trailingIcon}
    </button>
  );
});
